import { useState, useCallback } from 'react';
import { PromptEvaluation } from '../services/openRouterService';
import { Language } from './useLanguage';

export interface EvaluationHistoryItem {
  id: string;
  content: string;
  contentHash: string;
  evaluation: PromptEvaluation;
  timestamp: number;
  language: Language;
  model: string;
}

export const useEvaluationHistory = () => {
  const [history, setHistory] = useState<EvaluationHistoryItem[]>([]);

  // Generate content hash for comparison
  const generateContentHash = useCallback((content: string): string => {
    // Simple hash function for content comparison
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }, []);

  // Check if content has been evaluated recently
  const hasRecentEvaluation = useCallback((content: string, language: Language, model: string): EvaluationHistoryItem | null => {
    const contentHash = generateContentHash(content.trim());
    const cutoffTime = Date.now() - (15 * 60 * 1000); // 15 minutes ago
    
    const recentEvaluation = history.find(item => 
      item.contentHash === contentHash &&
      item.language === language &&
      item.model === model &&
      item.timestamp > cutoffTime
    );

    return recentEvaluation || null;
  }, [history, generateContentHash]);

  // Add evaluation to history
  const addEvaluation = useCallback((
    content: string,
    evaluation: PromptEvaluation,
    language: Language,
    model: string
  ) => {
    const contentHash = generateContentHash(content.trim());
    const newItem: EvaluationHistoryItem = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      content: content.trim(),
      contentHash,
      evaluation,
      timestamp: Date.now(),
      language,
      model
    };

    setHistory(prev => {
      // Remove old evaluation for the same content/language/model combination
      const filtered = prev.filter(item => 
        !(item.contentHash === contentHash && item.language === language && item.model === model)
      );
      
      // Keep only last 50 evaluations to prevent memory issues
      const updated = [newItem, ...filtered].slice(0, 50);
      
      return updated;
    });

    return newItem;
  }, [generateContentHash]);

  // Clear history
  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  // Get history for display
  const getHistory = useCallback(() => {
    return history.sort((a, b) => b.timestamp - a.timestamp);
  }, [history]);

  // Remove specific evaluation
  const removeEvaluation = useCallback((id: string) => {
    setHistory(prev => prev.filter(item => item.id !== id));
  }, []);

  return {
    history: getHistory(),
    hasRecentEvaluation,
    addEvaluation,
    clearHistory,
    removeEvaluation
  };
};